<script setup>
import { computed } from 'vue';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  inbox: {
    type: Object,
    default: () => ({}),
  },
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const referer = computed(() => props.conversationAttributes.referer);
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const contact = computed(() => props.conversation.meta?.sender || {});

const providerConfig = computed(() => props.inbox.provider_config || {});

const channelName = computed(() =>
  props.conversation.meta?.channel?.replace('Channel::', '')
);

const formatTimestamp = timestamp => {
  if (!timestamp) return '';
  const value = Number(timestamp);
  const date = new Date(
    (value < 10000000000 ? value * 1000 : value) || timestamp
  );

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formattedCreatedAt = computed(() =>
  formatTimestamp(props.conversation.created_at)
);

const formattedLastActivityAt = computed(() =>
  formatTimestamp(
    props.conversation.last_activity_at || props.conversation.timestamp
  )
);

const replyWindowStatus = computed(() => {
  if (channelName.value !== 'Whatsapp') return '';
  return props.conversation.can_reply
    ? 'Abierta: puedes responder libremente'
    : 'Cerrada: requiere plantilla de WhatsApp';
});

const whatsappElements = computed(() =>
  [
    {
      content: computed(() => contact.value.name),
      title: 'Contacto',
      key: 'static-contact-name',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: computed(() => contact.value.phone_number),
      title: 'Telefono del cliente',
      key: 'static-contact-phone',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: computed(() => props.inbox.name),
      title: 'Bandeja',
      key: 'static-inbox-name',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: channelName,
      title: 'Canal',
      key: 'static-channel',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: computed(() => props.inbox.provider),
      title: 'Proveedor',
      key: 'static-provider',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: computed(() => providerConfig.value.phone_number_id),
      title: 'Phone number ID',
      key: 'static-phone-number-id',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: computed(() => providerConfig.value.business_account_id),
      title: 'WABA ID',
      key: 'static-business-account-id',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: replyWindowStatus,
      title: 'Ventana de respuesta',
      key: 'static-reply-window',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: formattedCreatedAt,
      title: 'Creada',
      key: 'static-created-at',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: formattedLastActivityAt,
      title: 'Ultima actividad',
      key: 'static-last-activity-at',
      type: 'static_attribute',
      translate: false,
    },
    {
      content: computed(() => props.conversation.id),
      title: 'ID de conversacion',
      key: 'static-conversation-id',
      type: 'static_attribute',
      translate: false,
    },
  ].filter(attribute => !!attribute.content.value)
);

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
      key: 'static-platform',
      type: 'static_attribute',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
      key: 'static-ip-address',
      type: 'static_attribute',
    },
    ...whatsappElements.value,
  ].filter(attribute => !!attribute.content.value)
);
</script>

<template>
  <div class="conversation--details">
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.title"
          :title="
            element.translate === false ? element.title : $t(element.title)
          "
          :value="element.content.value"
        >
          <a
            v-if="element.key === 'static-referer'"
            :href="element.content.value"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand"
          >
            {{ element.content.value }}
          </a>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
